// =============================================================================
// ProviderWarehousesTab — Warehouse CRUD table + modal
// =============================================================================

import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Badge,
  Input,
  Modal,
  ModalFooter,
} from '@tkpod/ui';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Warehouse {
  id: string;
  name: string;
  code: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string | null;
  zip: string;
  country: string;
  timezone: string;
  cutoffLocalTime: string;
  handlingDaysMin: number;
  handlingDaysMax: number;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
}

interface ProviderWarehousesTabProps {
  providerId: string;
  warehouses: Warehouse[];
}

// ---------------------------------------------------------------------------
// Initial form state
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
  timezone: 'America/New_York',
  cutoffLocalTime: '14:00',
  handlingDaysMin: '1',
  handlingDaysMax: '3',
  isDefault: false,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProviderWarehousesTab({ providerId, warehouses }: ProviderWarehousesTabProps) {
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [archiveTarget, setArchiveTarget] = useState<Warehouse | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'provider', providerId] });

  const resetAndClose = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setShowModal(true);
  };

  const openEdit = (w: Warehouse) => {
    setEditingId(w.id);
    setForm({
      name: w.name,
      addressLine1: w.addressLine1,
      addressLine2: w.addressLine2 ?? '',
      city: w.city,
      state: w.state ?? '',
      zip: w.zip,
      country: w.country,
      timezone: w.timezone,
      cutoffLocalTime: w.cutoffLocalTime,
      handlingDaysMin: String(w.handlingDaysMin),
      handlingDaysMax: String(w.handlingDaysMax),
      isDefault: w.isDefault,
    });
    setShowModal(true);
  };

  const setField = (key: keyof typeof form) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // --- Mutations --------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      api.post(`/api/v1/admin/providers/${providerId}/warehouses`, data),
    onSuccess: () => { invalidate(); resetAndClose(); },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch(`/api/v1/admin/providers/${providerId}/warehouses/${id}`, data),
    onSuccess: () => { invalidate(); resetAndClose(); },
  });

  const archiveMutation = useMutation({
    mutationFn: async (id: string) =>
      api.delete(`/api/v1/admin/providers/${providerId}/warehouses/${id}`),
    onSuccess: () => { invalidate(); setArchiveTarget(null); },
  });

  const activeMutation = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const payload: Record<string, unknown> = {
      name: form.name,
      addressLine1: form.addressLine1,
      addressLine2: form.addressLine2 || undefined,
      city: form.city,
      state: form.state || undefined,
      zip: form.zip,
      country: form.country,
      timezone: form.timezone,
      cutoffLocalTime: form.cutoffLocalTime,
      handlingDaysMin: Number(form.handlingDaysMin) || 1,
      handlingDaysMax: Number(form.handlingDaysMax) || 3,
      isDefault: form.isDefault,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const mutationError = createMutation.error ?? updateMutation.error;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-text-primary">
          Warehouses ({warehouses.length})
        </h4>
        <Button variant="primary" size="sm" onClick={openCreate}>
          Add Warehouse
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-border">
        {warehouses.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
            No warehouses yet.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-secondary">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Cutoff</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Handling</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-surface">
              {warehouses.map((w) => (
                <tr key={w.id} className="transition-colors hover:bg-surface-secondary">
                  <td className="px-4 py-3 text-sm font-medium text-text-primary whitespace-nowrap">
                    {w.name}
                    {w.isDefault && (
                      <Badge variant="primary" size="sm" className="ml-2">Default</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                    {w.code ? (
                      <code className="rounded bg-surface-secondary px-1.5 py-0.5 text-xs">{w.code}</code>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                    {w.city}, {w.state ? `${w.state}, ` : ''}{w.country}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                    {w.cutoffLocalTime}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                    {w.handlingDaysMin}–{w.handlingDaysMax}d
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Badge variant={w.isActive ? 'success' : 'danger'} size="sm" dot>
                      {w.isActive ? 'Active' : 'Archived'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(w)}>
                        Edit
                      </Button>
                      {w.isActive && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setArchiveTarget(w)}
                        >
                          Archive
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit Modal */}
      <Modal
        open={showModal}
        onClose={resetAndClose}
        title={editingId ? 'Edit Warehouse' : 'Add Warehouse'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Name" value={form.name} onChange={setField('name')} required />

          <Input label="Address Line 1" value={form.addressLine1} onChange={setField('addressLine1')} required />
          <Input label="Address Line 2" value={form.addressLine2} onChange={setField('addressLine2')} />

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Input label="City" value={form.city} onChange={setField('city')} required />
            <Input label="State" value={form.state} onChange={setField('state')} />
            <Input label="ZIP" value={form.zip} onChange={setField('zip')} required />
            <Input label="Country" value={form.country} onChange={setField('country')} required />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input label="Timezone" value={form.timezone} onChange={setField('timezone')} required />
            <Input label="Cutoff Time" value={form.cutoffLocalTime} onChange={setField('cutoffLocalTime')} placeholder="14:00" />
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Input label="Handling Min (days)" type="number" value={form.handlingDaysMin} onChange={setField('handlingDaysMin')} />
              </div>
              <div className="flex-1">
                <Input label="Max" type="number" value={form.handlingDaysMax} onChange={setField('handlingDaysMax')} />
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={form.isDefault}
              onChange={(e) => setForm((prev) => ({ ...prev, isDefault: e.target.checked }))}
              className="rounded border-border"
            />
            Default warehouse for this provider
          </label>

          {mutationError && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
              {mutationError.message ?? 'Operation failed.'}
            </div>
          )}

          <ModalFooter>
            <Button variant="outline" type="button" onClick={resetAndClose}>Cancel</Button>
            <Button variant="primary" type="submit" loading={activeMutation} disabled={!form.name.trim()}>
              {editingId ? 'Save Changes' : 'Create Warehouse'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal
        open={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        title="Archive Warehouse"
        description={
          archiveTarget
            ? `Are you sure you want to archive "${archiveTarget.name}"? The warehouse will be set to inactive.`
            : undefined
        }
        size="sm"
      >
        <ModalFooter>
          <Button variant="outline" onClick={() => setArchiveTarget(null)}>Cancel</Button>
          <Button
            variant="danger"
            onClick={() => {
              if (archiveTarget) archiveMutation.mutate(archiveTarget.id);
            }}
            loading={archiveMutation.isPending}
          >
            Archive Warehouse
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
